import { useState } from 'react';
import { getAiModels, getAiSettings, putAiSettings } from '../../api/endpoints';
import type { AiProvider, AiSettings as AiSettingsData } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { RefreshButton } from '../../components/RefreshButton';
import { Spinner } from '../../components/Spinner';
import { useLoad } from '../../lib/useLoad';
import './ai.css';

const PROVIDER_LABEL: Record<AiProvider, string> = {
  claude: 'Anthropic Claude',
  openai: 'OpenAI',
};

function SettingsForm({
  settings,
  models,
  onSaved,
}: {
  settings: AiSettingsData;
  models: Record<AiProvider, string[]>;
  onSaved: (fresh: AiSettingsData) => void;
}) {
  const [provider, setProvider] = useState<AiProvider>(settings.provider);
  const [model, setModel] = useState(settings.model);
  const [apiKey, setApiKey] = useState('');
  const [removeKey, setRemoveKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const providerChanged = provider !== settings.provider;
  const providerModels = models[provider] ?? [];
  const effectiveModel = providerModels.includes(model) ? model : providerModels[0] ?? '';

  const switchProvider = (next: AiProvider) => {
    setProvider(next);
    setModel(models[next]?.[0] ?? '');
    setSaved(false);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const body: { provider: AiProvider; model: string; apiKey?: string | null } = {
        provider,
        model: effectiveModel,
      };
      if (removeKey) body.apiKey = null;
      else if (apiKey.trim()) body.apiKey = apiKey.trim();
      const fresh = await putAiSettings(body);
      onSaved(fresh);
      setApiKey('');
      setRemoveKey(false);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save AI settings.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Model</h2>
      <p className="manage-card-sub">
        Powers reply drafting and the chatbot for this company.
      </p>
      {error && <ErrorNote error={error} />}
      {saved && <Note tone="ok">AI settings saved.</Note>}

      <div className="ai-provider-row">
        {(Object.keys(models) as AiProvider[]).map((key) => (
          <label key={key} className={provider === key ? 'radio-pill active' : 'radio-pill'}>
            <input type="radio" checked={provider === key} onChange={() => switchProvider(key)} />
            {PROVIDER_LABEL[key] ?? key}
          </label>
        ))}
      </div>

      <Field label="Model">
        <select
          className="select"
          value={effectiveModel}
          onChange={(e) => {
            setModel(e.target.value);
            setSaved(false);
          }}
        >
          {providerModels.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </Field>

      <h3 className="bot-subhead">API key</h3>
      {settings.hasOwnApiKey && !removeKey ? (
        <Note tone="ok">
          Your own {PROVIDER_LABEL[settings.provider]} key is stored and in use.{' '}
          <button className="linklike" onClick={() => setRemoveKey(true)}>
            Remove it
          </button>
        </Note>
      ) : removeKey ? (
        <Note tone="warn">
          The stored key will be removed on save — usage falls back to the platform key.{' '}
          <button className="linklike" onClick={() => setRemoveKey(false)}>
            Keep it
          </button>
        </Note>
      ) : (
        <p className="manage-card-sub">
          Without your own key, usage runs on the platform’s shared key.
        </p>
      )}
      {!removeKey && (
        <Field
          label={settings.hasOwnApiKey ? 'Replace key' : 'Add your key'}
          hint="Stored encrypted. Leave blank to keep things as they are."
        >
          <input
            className="input"
            type="password"
            value={apiKey}
            // Chrome ignores autocomplete="off" on a password input and fills
            // whatever it has saved for this origin — an API key field would
            // otherwise be handed someone's password. Same treatment the
            // channel credential fields get (see connect/fields.tsx).
            autoComplete="new-password"
            data-1p-ignore
            data-lpignore="true"
            placeholder={provider === 'claude' ? 'sk-ant-…' : 'sk-…'}
            onChange={(e) => {
              setApiKey(e.target.value);
              setSaved(false);
            }}
          />
        </Field>
      )}
      {providerChanged && settings.hasOwnApiKey && !apiKey.trim() && !removeKey && (
        <Note tone="warn">
          Your stored key belongs to {PROVIDER_LABEL[settings.provider]}. Switching to{' '}
          {PROVIDER_LABEL[provider]} without entering a matching key drops it, and usage falls back
          to the platform key.
        </Note>
      )}

      <div className="manage-card-actions">
        <button className="btn btn-primary" onClick={() => void save()} disabled={busy}>
          {busy && <Spinner />}
          Save AI settings
        </button>
      </div>
    </section>
  );
}

export function AiSettings() {
  const settings = useLoad(getAiSettings);
  const models = useLoad(getAiModels);

  return (
    <main className="page page-narrow">
      <div className="page-head">
        <div>
          <h1 className="page-title">AI</h1>
          <p className="page-sub">The model behind reply drafting and the chatbot.</p>
        </div>
        {/* Mostly for the model list — what a provider offers changes without
            anything happening in here. */}
        <RefreshButton
          refreshing={settings.refreshing || models.refreshing}
          busy={settings.stale || models.stale}
          onClick={() => {
            settings.reload();
            models.reload();
          }}
        />
      </div>

      {settings.stale || models.stale ? (
        <div className="home-loading">
          <Spinner large />
        </div>
      ) : settings.error ? (
        <ErrorNote error={settings.error} />
      ) : models.error ? (
        <ErrorNote error={models.error} />
      ) : settings.data && models.data ? (
        <SettingsForm
          key={`${settings.data.provider}-${settings.data.hasOwnApiKey}`}
          settings={settings.data}
          models={models.data.models}
          onSaved={settings.setData}
        />
      ) : null}
    </main>
  );
}
